import { Link, NavLink, useParams } from 'react-router-dom'
import { loadPerson } from '@/data/queries'
import { loadPortalBadges } from '@/data/portal'
import { useAsync } from '@/data/useAsync'
import { Failed, Loading } from '@/components/bits'
import { useOrg } from '@/org/OrgProvider'
import { today } from '@/lib/format'
import { Overview, lifecycleOf, LIFECYCLE_LABEL } from './person/Overview'
import { Reviews } from './person/Reviews'
import { Pay } from './person/Pay'
import { Hours } from './person/Hours'
import { Equipment } from './person/Equipment'
import { Toil } from './person/Toil'

export { lifecycleOf } from './person/Overview'

type Tab = 'overview' | 'reviews' | 'pay' | 'hours' | 'toil' | 'equipment'

const TABS: { key: Tab; path: string; label: string }[] = [
  { key: 'overview', path: '', label: 'Overview' },
  { key: 'reviews', path: 'reviews', label: 'Supervisions and appraisals' },
  { key: 'pay', path: 'pay', label: 'Pay' },
  { key: 'hours', path: 'hours', label: 'Contracted hours' },
  { key: 'toil', path: 'toil', label: 'TOIL' },
  { key: 'equipment', path: 'equipment', label: 'Equipment' },
]

/**
 * One person, six tabs.
 *
 * The person loads once here rather than in each tab, because every tab needs
 * the same identity and the same department, and a tab strip that reloads the
 * header on every click feels broken even when it is not.
 */
export function Person() {
  const { current } = useOrg()
  const params = useParams<{ employmentId: string; '*': string }>()
  const employmentId = params.employmentId
  const tab = (params['*'] ?? '').split('/')[0] ?? ''

  const person = useAsync(
    () => loadPerson(current.id, employmentId ?? ''),
    [current.id, employmentId],
  )
  const badges = useAsync(
    () => loadPortalBadges(current.id, employmentId ?? '', today()),
    [current.id, employmentId],
  )

  if (employmentId === undefined) return <Failed message="No employment in the address." />
  if (person.state === 'loading') return <Loading what="this person" />
  if (person.state === 'error') return <Failed message={person.message} onRetry={person.reload} />

  const d = person.data
  const name = `${d.person.preferred_name ?? d.person.first_name} ${d.person.last_name}`
  const lifecycle = lifecycleOf(d.employment.end_date, today())
  const b = badges.state === 'ready' ? badges.data : null

  function reloadAll() {
    person.reload()
    badges.reload()
  }

  return (
    <>
      <div style={{ padding: 'var(--s-5) var(--s-6) 0' }}>
        <Link
          to="/people"
          className="btn quiet sm"
          style={{ display: 'inline-block', marginBottom: 16 }}
        >
          &larr; All people
        </Link>
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 14, flexWrap: 'wrap' }}>
          <h1 style={{ fontSize: 26 }}>{name}</h1>
          <span className={`status ${lifecycle === 'terminated' ? 'terminated' : lifecycle}`}>
            {LIFECYCLE_LABEL[lifecycle]}
          </span>
        </div>
        <div style={{ fontSize: 13, color: 'var(--ivory-dim)', margin: '4px 0 16px' }}>
          {[d.department, d.position].filter((x) => x !== null).join(' · ') || 'No department'}
          {d.employment.employee_reference !== null && (
            <>
              {' · '}
              <span className="mono">{d.employment.employee_reference}</span>
            </>
          )}
        </div>
      </div>

      <nav className="tabs" aria-label="Team member record">
        {TABS.map((t) => (
          <NavLink
            key={t.key}
            to={t.path === '' ? `/people/${employmentId}` : `/people/${employmentId}/${t.path}`}
            end={t.path === ''}
          >
            {t.label}
            {t.key === 'equipment' && b !== null && b.equipmentOut > 0 && (
              <span className={b.equipmentOverdue > 0 ? 'badge attention' : 'badge'}>
                {b.equipmentOut}
              </span>
            )}
            {t.key === 'reviews' && b !== null && b.concernsRaised > 0 && (
              <span className="badge attention">{b.concernsRaised}</span>
            )}
          </NavLink>
        ))}
      </nav>

      <div className="screen">
        {tab === '' && <Overview person={d} onSaved={reloadAll} />}
        {tab === 'reviews' && <Reviews person={d} />}
        {tab === 'pay' && <Pay person={d} />}
        {tab === 'hours' && <Hours person={d} />}
        {tab === 'toil' && <Toil person={d} />}
        {tab === 'equipment' && <Equipment person={d} />}
      </div>
    </>
  )
}
